import { useState, type UIEvent } from 'react';
import { HOME_EMPTY_LINE, HOME_LAYOUT } from '@/lib/constants/appPreview';
import type { AppV1Browse, AppV1Cfg, AppV1Item, AppV1Row } from '@/lib/types/AppV1';
import {
  AppPreviewUtils,
  BILLBOARD_HEIGHT_FHD,
  LEFT_SCRIM_WIDTH_FHD,
  NAVBAR_HEIGHT_FHD,
  NAV_SCRIM_HEIGHT_FHD,
  PAGE_SCRIM_TOP_FHD,
  SCREEN_HEIGHT_FHD,
  SCREEN_WIDTH_FHD,
} from '../AppPreviewUtils';
import { AppTvBillboard } from './AppTvBillboard';

interface AppTvFrameProps {
  cfg: AppV1Cfg;
  browse: AppV1Browse;
  /** The frame's widest size in CSS pixels (the geometry scales with it). */
  maxWidth?: number;
  /**
   * The Home image to draw instead of the payload's: a URL shows that image
   * whether or not the app's switch is on (the Brand Kit tab shows the result
   * before it is turned on); null draws none. Omitted = `cfg.theme.backdrop`.
   */
  backdropUrl?: string | null;
}

/**
 * The simulated TV screen: the home the shells draw from the same cfg + browse
 * (design: ott/design system/surfaces.md "Home"; ADR-0016). Colors and spacing
 * come from the live theme payload, so everything is inline-styled — Mantine
 * theming must not leak into the simulated TV canvas.
 *
 * No blur anywhere. Layers, bottom to top: the plain ground → the full-screen
 * PICTURE when there is one (the customer's Home image as uploaded, in every
 * layout; or, in `billboard` under Immersive / Full-bleed, the one item's raw
 * poster covering the screen) → the left scrim (960, smoothstep) → the page
 * scrim (0 % at 180 → 40 % at 648 → 85 % at 1080) only when rows sit on the
 * picture → the hero-state nav gradient → content. While a picture shows the
 * band draws TEXT ONLY; on the plain ground it draws the hero style's art.
 *
 * Scrolling is the content-bounded slide (D1): the frame scrolls by exactly
 * `clamp(contentBottom − 1080, 0, 520)` — 1 rail nothing, 2 rails 308, 3+ the
 * full 520. The NavBar sticks as the opaque browse-state strip once slid; the
 * hero (and the picture) stays drawn under a partial slide and fades out only
 * on the full one.
 */
export function AppTvFrame({ cfg, browse, maxWidth = 960, backdropUrl }: AppTvFrameProps) {
  const { colors } = cfg.theme;
  const { fhd } = AppPreviewUtils;
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [heroIdx, setHeroIdx] = useState(0);
  // How far the page has slid, in FHD pixels (0 = the hero state the preview opens on).
  const [slidFhd, setSlidFhd] = useState(0);
  // An image that fails to load leaves its layer empty — never a broken icon, never a retry.
  const [failed, setFailed] = useState<Record<string, true>>({});
  const [loaded, setLoaded] = useState<Record<string, true>>({});
  const markFailed = (url: string) => setFailed((m) => (m[url] ? m : { ...m, [url]: true }));
  const markLoaded = (url: string) => setLoaded((m) => (m[url] ? m : { ...m, [url]: true }));

  const layout = AppPreviewUtils.homeLayout(browse);
  const rows = AppPreviewUtils.layoutRows(layout, browse);
  const heroRow = AppPreviewUtils.heroRow(rows);
  const railRows = AppPreviewUtils.railRows(rows);
  const heroItem = heroRow ? heroRow.items[Math.min(heroIdx, heroRow.items.length - 1)] : null;
  const style = heroRow ? AppPreviewUtils.heroStyle(heroRow) : 'card';

  const homeImageUrl = backdropUrl === undefined ? AppPreviewUtils.backdropUrl(cfg) : backdropUrl;
  const wanted = AppPreviewUtils.homePicture(layout, style, heroItem, homeImageUrl);
  // a picture that failed to load shows the ground, and the band renders its style as if nothing were on
  const picture = wanted && !failed[wanted.url] ? wanted : null;
  const slide = AppPreviewUtils.slideFhd(railRows);
  const slid = slidFhd > 0;
  const heroHidden = AppPreviewUtils.heroFadedOut(slidFhd);

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const next = el.clientWidth > 0 ? Math.round((el.scrollTop * SCREEN_WIDTH_FHD) / el.clientWidth) : 0;
    if (next !== slidFhd) setSlidFhd(next);
  };

  // NavBar (rokucore nav/NavBar.xml): 1920x120 strip, brand logo at [96,10]
  // 400x100, tabs from x=560 with 48px spacing. In the hero state it is
  // transparent on the nav gradient over the band's top (ADR-0012); once the
  // page slides it is the opaque browse-state strip, pinned to the top — the
  // hero is cut under it. With no billboard it is the opaque strip from the start.
  const navBar = (
    <div
      data-testid="tv-navbar"
      data-slid={slid ? 'true' : undefined}
      style={{
        position: heroRow ? 'sticky' : 'relative',
        top: 0,
        left: 0,
        width: '100%',
        height: fhd(NAVBAR_HEIGHT_FHD),
        // the band starts at y=0 under the strip
        marginBottom: heroRow ? `-${fhd(NAVBAR_HEIGHT_FHD)}` : undefined,
        backgroundColor: heroRow && !slid ? 'transparent' : colors.bgPrimary,
        display: 'flex',
        alignItems: 'center',
        zIndex: 2,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: fhd(96),
          top: fhd(10),
          width: fhd(400),
          height: fhd(100),
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {cfg.brand.logoUrl ? (
          <img
            src={cfg.brand.logoUrl}
            alt={cfg.brand.name}
            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span
            style={{
              color: colors.textPrimary,
              fontSize: fhd(36),
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {cfg.brand.name}
          </span>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          left: fhd(560),
          top: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: fhd(48),
        }}
      >
        {cfg.nav.map((navItem, idx) => (
          <span
            key={navItem.navId}
            style={{
              color: idx === 0 ? colors.accent : colors.textSecondary,
              fontSize: fhd(30),
              fontWeight: idx === 0 ? 600 : 400,
              whiteSpace: 'nowrap',
            }}
          >
            {navItem.title}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div
      data-testid="tv-home-frame"
      data-home-layout={layout}
      data-hero-style={heroRow ? style : undefined}
      style={{
        aspectRatio: '16 / 9',
        width: '100%',
        maxWidth,
        backgroundColor: colors.bgPrimary,
        border: '10px solid #17181c',
        borderRadius: 14,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden',
        // FHD geometry below is expressed in cqw units against this frame.
        containerType: 'inline-size',
        position: 'relative',
      }}
    >
      {picture && (
        <img
          key={picture.url}
          src={picture.url}
          alt=""
          aria-hidden
          data-testid="tv-picture"
          data-picture-kind={picture.kind}
          onLoad={() => markLoaded(picture.url)}
          onError={() => markFailed(picture.url)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            // as uploaded, scaled and cropped to cover the screen: sharp, full size
            objectFit: 'cover',
            // fades in over the ground (style_core "Imagery"); leaves with the hero on a full slide
            opacity: loaded[picture.url] && !heroHidden ? 1 : 0,
            transition: 'opacity 240ms ease',
          }}
        />
      )}
      {picture && (
        // the one left scrim: full height, 960 wide, static in both scroll states
        <div
          data-testid="tv-left-scrim"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: fhd(LEFT_SCRIM_WIDTH_FHD),
            height: '100%',
            background: AppPreviewUtils.leftScrimGradient(colors.bgPrimary),
          }}
        />
      )}
      {AppPreviewUtils.pageScrimShown(picture, railRows.length) && (
        <div
          data-testid="tv-page-scrim"
          style={{
            position: 'absolute',
            left: 0,
            // one static ramp, y=180 → 1080: the same in the hero and the browse state
            top: fhd(PAGE_SCRIM_TOP_FHD),
            width: '100%',
            height: fhd(SCREEN_HEIGHT_FHD - PAGE_SCRIM_TOP_FHD),
            background: AppPreviewUtils.pageScrimGradient(colors.bgPrimary),
          }}
        />
      )}

      <div
        onScroll={onScroll}
        style={{
          position: 'absolute',
          inset: 0,
          // the slide is the content's: nothing moves when the rows fit (1 rail, billboard, empty)
          overflowY: slide > 0 ? 'auto' : 'hidden',
          overflowX: 'hidden',
          // A TV has no scrollbar: the second-row peek is the "more below" signal.
          scrollbarWidth: 'none',
        }}
      >
        {/* the page: exactly one screen plus the slide, so the scroll stops where the shells stop
            (no overflow here — the sticky strip binds to the nearest overflow ancestor, the scroller) */}
        <div data-testid="tv-page" style={{ position: 'relative', height: fhd(SCREEN_HEIGHT_FHD + slide) }}>
          {navBar}

          {layout === HOME_LAYOUT.EMPTY ? (
            // Nothing is published: the brand and one line, centred on the screen.
            <div
              data-testid="tv-home-empty"
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {cfg.brand.logoUrl ? (
                <img
                  src={cfg.brand.logoUrl}
                  alt={cfg.brand.name}
                  style={{ maxWidth: fhd(400), maxHeight: fhd(100), objectFit: 'contain' }}
                />
              ) : (
                <span style={{ color: colors.textPrimary, fontSize: fhd(44), fontWeight: 700 }}>{cfg.brand.name}</span>
              )}
              <span style={{ marginTop: fhd(32), color: colors.textSecondary, fontSize: fhd(30) }}>{HOME_EMPTY_LINE}</span>
            </div>
          ) : (
            <>
              {heroRow && (
                <div
                  data-testid="tv-hero"
                  style={{
                    position: 'relative',
                    // stays through a partial slide (cut under the strip); gone on the full one
                    opacity: heroHidden ? 0 : 1,
                    transition: 'opacity 240ms ease',
                  }}
                >
                  <AppTvBillboard
                    cfg={cfg}
                    row={heroRow}
                    index={heroIdx}
                    onIndexChange={setHeroIdx}
                    art={AppPreviewUtils.heroArt(style, picture != null)}
                  />
                  {/* hero-state nav gradient (ADR-0012) over the band's top; the strip above is transparent on it */}
                  <div
                    data-testid="hero-nav-scrim"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: fhd(NAV_SCRIM_HEIGHT_FHD),
                      background: AppPreviewUtils.navScrimGradient(colors.bgPrimary),
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              )}

              {/* rails (rokucore HomeView/RowList): 96px content margin, rows clip at
                  x=1824 like the RowList's 1728px row, 24px art-to-art gap, 40px
                  inter-row spacing + 24px row pad. No scrollbar: a TV row simply runs
                  off the right edge (trackpad/shift-wheel still scrolls it). `billboard`
                  has no rail at all. */}
              {layout !== HOME_LAYOUT.BILLBOARD && (
                <div
                  style={{
                    // Hero state: Rows.bs HeroRowsY (second-row peek at the fold; a
                    // single rail keeps the 700 origin); no billboard: rows at y=180
                    // under the 120px NavBar.
                    paddingTop: heroRow
                      ? fhd(AppPreviewUtils.heroRowsY(railRows.map(AppPreviewUtils.rowHeightFhd), railRows.length) - BILLBOARD_HEIGHT_FHD)
                      : fhd(60),
                    paddingLeft: fhd(96),
                    // the rest of the page: rows past the slide are clipped, as the shells' slide stops there
                    height: fhd(SCREEN_HEIGHT_FHD + slide - (heroRow ? BILLBOARD_HEIGHT_FHD : NAVBAR_HEIGHT_FHD)),
                    overflow: 'hidden',
                  }}
                >
                  {rows.length === 0 && (
                    <span style={{ color: colors.textSecondary, fontSize: fhd(27) }}>
                      No content rows yet — organize media into categories to fill the home screen.
                    </span>
                  )}

                  {railRows.map((row) => {
                    const size = AppPreviewUtils.cardSizeFhd(row.itemAspect);
                    const belowCard = row.titleTreatment === 'below_card';
                    return (
                      <div key={row.rowId} style={{ marginBottom: fhd(14) }}>
                        {row.title && (
                          <div
                            style={{
                              color: colors.textPrimary,
                              fontSize: fhd(30),
                              lineHeight: fhd(36),
                              fontWeight: 500,
                              marginBottom: fhd(14),
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {row.title}
                          </div>
                        )}
                        <div
                          style={{
                            width: fhd(1728),
                            display: 'flex',
                            gap: fhd(24),
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            scrollbarWidth: 'none',
                            // Room for the outside focus ring (2px gap + 4px ring).
                            padding: fhd(6),
                            margin: `-${fhd(6)}`,
                            // Below-card titles reserve a measured 2-line block (104px).
                            minHeight: belowCard ? fhd(size.h + 104) : fhd(size.h),
                          }}
                        >
                          {row.items.map((rowItem) => (
                            <TvCard
                              key={rowItem.idBC}
                              cfg={cfg}
                              row={row}
                              item={rowItem}
                              size={size}
                              focused={focusedKey === `${row.rowId}:${rowItem.idBC}`}
                              onFocus={() => setFocusedKey(`${row.rowId}:${rowItem.idBC}`)}
                              onBlur={() => setFocusedKey(null)}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface TvCardProps {
  cfg: AppV1Cfg;
  row: AppV1Row;
  item: AppV1Item;
  size: { w: number; h: number };
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Mirror of rokucore CardItem: square crisp art (adr_0004), badge at [10,10]
 * 30px tall, below-card title in the small system font 12px under the art
 * (max 2 lines), and the focus ring OUTSIDE the art — 2px page gap + ring —
 * with no card scale-up (adr_0007 v2).
 */
function TvCard({ cfg, row, item, size, focused, onFocus, onBlur }: TvCardProps) {
  const { colors, focus } = cfg.theme;
  const { fhd } = AppPreviewUtils;
  const artworkUrl = AppPreviewUtils.artworkUrl(item, row.itemAspect);
  const ringPx = Math.min(focus.ringWidthPx, 4);
  const badge = item.badge;
  const badgeBg =
    badge === 'live' ? colors.badgeLive : badge === 'new' ? colors.badgeNew : colors.bgOverlay;

  return (
    <div
      onMouseEnter={onFocus}
      onMouseLeave={onBlur}
      style={{ flex: '0 0 auto', width: fhd(size.w), cursor: 'default' }}
    >
      <div
        style={{
          position: 'relative',
          width: fhd(size.w),
          height: fhd(size.h),
          backgroundColor: colors.bgSurface,
          overflow: 'hidden',
          outline: focused ? `${fhd(ringPx)} solid ${colors.focusRing}` : 'none',
          outlineOffset: fhd(2),
        }}
      >
        {artworkUrl ? (
          <img
            src={AppPreviewUtils.sizedArtworkUrl(artworkUrl, 640)}
            alt={item.title}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.textSecondary,
              fontSize: fhd(24),
              padding: fhd(12),
              textAlign: 'center',
            }}
          >
            {item.title}
          </div>
        )}
        {badge && (
          <div
            style={{
              position: 'absolute',
              top: fhd(10),
              left: fhd(10),
              height: fhd(30),
              padding: `0 ${fhd(11)}`,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: badgeBg,
              color: colors.textOnAccent,
              fontSize: fhd(22),
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {badge}
          </div>
        )}
      </div>
      {row.titleTreatment === 'below_card' && (
        <div
          style={{
            color: focused ? colors.textPrimary : colors.textSecondary,
            fontSize: fhd(27),
            lineHeight: fhd(40),
            marginTop: fhd(12),
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.title}
        </div>
      )}
    </div>
  );
}
